import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiUser, FiMapPin, FiCreditCard, FiShoppingBag, FiArrowRight, FiX } from 'react-icons/fi';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { useStoreSettings } from '../context/StoreSettingsContext';
import { orderAPI } from '../api/services';
import './Checkout.css';

function Checkout() {
  const { items, getCartTotal, clearCart } = useCartStore();
  const { user, token, updateProfile } = useAuthStore();
  const { settings } = useStoreSettings();
  const allMethods    = settings?.payment_methods || [];
  const enabledMethods = allMethods.filter(m => m.enabled !== false);
  const whatsappNumber = settings?.whatsapp_number || '';
  const shippingCost  = Number(settings?.shipping?.cost ?? 200);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [showWaBtn, setShowWaBtn] = useState(true);
  const [phoneError, setPhoneError] = useState('');
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    delivery_address: user?.address || '',
    phone: user?.phone || '',
    city: user?.city || '',
    postal_code: user?.postal_code || '',
  });

  const validatePhone = (val) => {
    const digits = val.replace(/\s|-/g, '');
    if (!digits) return 'Phone number is required';
    if (digits.startsWith('+92') && digits.length === 13) return '';
    if (digits.startsWith('92')  && digits.length === 12) return '';
    if (digits.startsWith('03')  && digits.length === 11) return '';
    return 'Enter a valid Pakistani number (e.g. 03001234567)';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'phone') setPhoneError(validatePhone(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validatePhone(formData.phone);
    if (err) { setPhoneError(err); return; }
    setLoading(true);
    const fullAddress = [formData.delivery_address, formData.city, formData.postal_code]
      .filter(Boolean).join(', ');

    try {
      if (token) {
        await updateProfile({
          address: formData.delivery_address,
          phone: formData.phone,
          city: formData.city,
          postal_code: formData.postal_code,
        });
        const response = await orderAPI.createOrder({
          delivery_address: fullAddress,
          phone: formData.phone,
          items: items.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
        });
        await clearCart();
        navigate(`/orders/${response.data.id}`);
      } else {
        const response = await orderAPI.createGuestOrder({
          guest_name: formData.guest_name,
          guest_email: formData.guest_email,
          delivery_address: fullAddress,
          phone: formData.phone,
          items: items.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
        });
        await clearCart();
        setOrderSuccess(response.data);
      }
    } catch {
      alert('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (orderSuccess) {
    const waNumber = whatsappNumber.replace(/\D/g, '');
    const waText = encodeURIComponent(`Hi, Order #${orderSuccess.id} - sending payment proof.`);
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="co-success-wrap">
            <div className="co-success-card">

              {/* Check + title */}
              <div className="co-success-icon">✓</div>
              <h1>Order Placed!</h1>
              <p className="co-success-ordernum">Order #{orderSuccess.id}</p>
              <p className="co-success-msg">
                Thank you{orderSuccess.guest_name ? <>, <strong>{orderSuccess.guest_name}</strong></> : ''}!
                {orderSuccess.guest_email && <> We'll send updates to <strong>{orderSuccess.guest_email}</strong>.</>}
              </p>

              {/* Banking + WhatsApp section */}
              {paymentMethod === 'online' && enabledMethods.length > 0 && (
                <div className="co-success-payment">
                  <p className="co-success-payment-heading">Complete Your Payment</p>
                  <p className="co-success-payment-sub">Transfer the amount to one of the accounts below, then send your payment proof on WhatsApp with your order number.</p>

                  {/* Bank accounts */}
                  <div className="co-success-accounts">
                    {enabledMethods.map((pm, i) => (
                      <div key={pm.id || i} className="co-success-account">
                        <p className="co-success-account-label">{pm.label || pm.type}</p>
                        {pm.type === 'bank' && pm.bank_name && (
                          <div className="co-success-account-row">
                            <span>Bank</span><strong>{pm.bank_name}</strong>
                          </div>
                        )}
                        <div className="co-success-account-row">
                          <span>Account No.</span><strong>{pm.account_number}</strong>
                        </div>
                        <div className="co-success-account-row">
                          <span>Account Title</span><strong>{pm.account_title}</strong>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* WhatsApp CTA */}
                  {waNumber && (
                    <a
                      href={`https://wa.me/${waNumber}?text=${waText}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="co-success-wa-cta"
                      onClick={e => { e.stopPropagation(); }}
                    >
                      Send Order #{orderSuccess.id} Payment Proof on WhatsApp
                    </a>
                  )}
                </div>
              )}

              <Link to="/products" className="co-success-btn">
                Continue Shopping <FiArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = getCartTotal();
  const shipping = shippingCost;
  const total = subtotal + shipping;
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="co-header">
          <h1>Checkout</h1>
        </div>

        <div className="checkout-layout">
          <form className="co-form-card" onSubmit={handleSubmit}>

            {!token && (
              <section className="co-section">
                <div className="co-section-header">
                  <div className="co-section-icon"><FiUser size={17} /></div>
                  <p className="co-section-title">Your Details</p>
                </div>
                <div className="co-form-row">
                  <div className="co-input-group">
                    <label className="co-label">Full Name *</label>
                    <input className="co-input" type="text" name="guest_name" value={formData.guest_name} onChange={handleChange} placeholder="Your full name" required />
                  </div>
                  <div className="co-input-group">
                    <label className="co-label">Email *</label>
                    <input className="co-input" type="email" name="guest_email" value={formData.guest_email} onChange={handleChange} placeholder="your@email.com" required />
                  </div>
                </div>
              </section>
            )}

            <section className="co-section">
              <div className="co-section-header">
                <div className="co-section-icon"><FiMapPin size={17} /></div>
                <p className="co-section-title">Delivery Information</p>
              </div>
              <div className="co-input-group">
                <label className="co-label">Delivery Address *</label>
                <textarea className="co-input" name="delivery_address" value={formData.delivery_address} onChange={handleChange} placeholder="Enter your complete address" rows="3" required />
              </div>
              <div className="co-form-row">
                <div className="co-input-group">
                  <label className="co-label">City *</label>
                  <input className="co-input" type="text" name="city" value={formData.city} onChange={handleChange} placeholder="City" required />
                </div>
                <div className="co-input-group">
                  <label className="co-label">Postal Code</label>
                  <input className="co-input" type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} placeholder="Postal Code" />
                </div>
              </div>
              <div className="co-input-group">
                <label className="co-label">Phone Number *</label>
                <input
                  className={`co-input${phoneError ? ' co-input-error' : ''}`}
                  type="tel" name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="03001234567"
                  required
                />
                {phoneError && <p className="co-field-error">{phoneError}</p>}
              </div>
            </section>

            <section className="co-section">
              <div className="co-section-header">
                <div className="co-section-icon"><FiCreditCard size={17} /></div>
                <p className="co-section-title">Payment Method</p>
              </div>
              <div className="co-pay-options">
                <label
                  className={`co-pay-option${paymentMethod === 'online' ? ' selected' : ''}`}
                  onClick={() => setPaymentMethod('online')}
                >
                  <input type="radio" name="payment" value="online" checked={paymentMethod === 'online'} onChange={() => setPaymentMethod('online')} />
                  <div className="co-pay-dot" />
                  <div>
                    <p className="co-pay-label">Online Banking</p>
                    <p className="co-pay-sub">Debit / Credit Card</p>
                  </div>
                </label>
                {paymentMethod === 'online' && enabledMethods.length > 0 && (
                  <div className="co-bank-box">
                    {enabledMethods.map((pm, i) => (
                      <div key={pm.id || i} className={`co-bank-method${i < enabledMethods.length - 1 ? ' co-bank-method-divider' : ''}`}>
                        <p className="co-bank-method-label">{pm.label || pm.type}</p>
                        {pm.type === 'bank' && pm.bank_name && (
                          <div className="co-bank-row">
                            <span className="co-bank-key">Bank</span>
                            <span className="co-bank-val">{pm.bank_name}</span>
                          </div>
                        )}
                        <div className="co-bank-row">
                          <span className="co-bank-key">Account Number</span>
                          <span className="co-bank-val">{pm.account_number}</span>
                        </div>
                        <div className="co-bank-row">
                          <span className="co-bank-key">Account Title</span>
                          <span className="co-bank-val">{pm.account_title}</span>
                        </div>
                      </div>
                    ))}
                    {whatsappNumber && (
                      <div className="co-bank-instruction">
                        <span className="co-bank-instruction-icon">!</span>
                        <p>
                          After placing your order, send your <strong>order number</strong> and <strong>payment invoice</strong> to{' '}
                          <a
                            href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=Hi%2C+sending+payment+proof`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                          >
                            WhatsApp
                          </a>
                          {' '}to confirm your order.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <label
                  className={`co-pay-option${paymentMethod === 'cod' ? ' selected' : ''}`}
                  onClick={() => setPaymentMethod('cod')}
                >
                  <input type="radio" name="payment" value="cod" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} />
                  <div className="co-pay-dot" />
                  <div>
                    <p className="co-pay-label">Cash on Delivery</p>
                    <p className="co-pay-sub">Pay when your order arrives</p>
                  </div>
                </label>
              </div>
            </section>

            <div className="co-submit-section">
              <button
                type="submit"
                className="co-submit-btn"
                disabled={loading || items.length === 0}
              >
                {loading ? 'Placing Order…' : (<>Place Order <FiArrowRight size={18} /></>)}
              </button>
            </div>
          </form>

          <aside className="co-summary-card">
            <div className="co-summary-header">
              <FiShoppingBag size={18} />
              <div>
                <p className="co-summary-title">Order Summary</p>
                <p className="co-summary-count">{count} {count === 1 ? 'item' : 'items'}</p>
              </div>
            </div>

            <div className="co-summary-products">
              {items.map((item) => {
                const price = (item.product?.discount_price && item.product?.discount_active !== false ? item.product.discount_price : null) || item.product?.price || 0;
                return (
                  <div key={item.id} className="co-summary-item">
                    <div className="co-summary-item-img-wrap">
                      <img
                        src={item.product?.image_url || 'https://placehold.co/52x52?text=?'}
                        alt={item.product?.name}
                        className="co-summary-item-img"
                      />
                      <span className="co-summary-item-qty">{item.quantity}</span>
                    </div>
                    <p className="co-summary-item-name">{item.product?.name}</p>
                    <span className="co-summary-item-price">Rs {(price * item.quantity).toFixed(0)}</span>
                  </div>
                );
              })}
            </div>

            <div className="co-summary-calcs">
              <div className="co-calc-row">
                <span>Subtotal</span>
                <span>Rs {subtotal.toFixed(0)}</span>
              </div>
              <div className="co-calc-row">
                <span>Shipping</span>
                <span>Rs {shipping.toFixed(0)}</span>
              </div>
              <div className="co-calc-row co-total">
                <span>Total</span>
                <span className="co-total-val">Rs {total.toFixed(0)}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
